import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
  LinearProgress,
  Link,
} from '@mui/material';
import {
  ExpandMore,
  TrendingUp,
  TrendingDown,
  LockClock,
  Refresh,
  RocketLaunch,
  Bolt,
  InfoOutlined,
  Newspaper,
  HourglassBottom,
  FlashOn,
  Public,
  Radar,
  AddCircleOutline,
  TrendingUpOutlined,
  AutoAwesome,
  OpenInNew,
} from '@mui/icons-material';
import apiService from '../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface MarketData {
  price: number | null;
  prev_close: number | null;
  change_pct: number | null;
  week_change_pct: number | null;
  market_cap: number | null;
  fifty_two_week_high: number | null;
  fifty_two_week_low: number | null;
  data_available: boolean;
}

interface LockupInfo {
  ipo_date: string;
  lockup_expiry: string;
  days_since_ipo: number;
  days_until_lockup_expiry: number;
  status: 'pre_ipo' | 'recent_ipo' | 'lockup_watch' | 'post_lockup' | 'normal';
}

interface Stock {
  symbol: string;
  name: string;
  description: string;
  disruption: string;
  stage: string;
  ipo_date?: string;
  tags: string[];
  market_data?: MarketData;
  lockup?: LockupInfo | null;
  /** 'curated' = written by hand; 'classifier' = the news LLM agent placed it here */
  source?: 'curated' | 'classifier' | 'manual';
  evidence?: { article_title?: string; article_source?: string; article_url?: string; confidence?: number; origin?: string } | null;
  added_at?: string | null;
  status?: 'active' | 'retired';
  retired_reason?: string | null;
  pulse?: { news_30d?: number | null; last_news_at?: string | null; last_headline?: string | null;
            last_headline_url?: string | null; last_headline_source?: string | null } | null;
}

interface Category {
  id: string;
  name: string;
  emoji: string;
  color: string;
  description: string;
  why_it_matters: string;
  stocks: Stock[];
  /** 'curated' = seeded by hand; 'discovered' = created by the discovery radar + LLM writer */
  source?: 'curated' | 'discovered';
  created_at?: string | null;
  pulse?: { news_30d?: number | null; text?: string | null; updated_at?: string | null } | null;
}

interface NewsBuzz {
  article_count_7d: number;
  latest_article_at: string | null;
  latest_headline: { title: string; source: string; published_at: string } | null;
}

interface PreIpoCompany {
  id: string;
  name: string;
  ticker?: string;
  expected_ticker?: string;
  category: string;
  stage: 'ipo_imminent' | 'recent_ipo' | 'just_ipost' | 'pre_ipo';
  ipo_date?: string;
  days_to_ipo?: number;
  lockup?: LockupInfo | null;
  market_data?: { price: number | null; market_cap: number | null; is_trading: boolean };
  news_buzz: NewsBuzz;
  last_known_valuation_b?: number;
  key_investors?: string[];
  description: string;
  ipo_signals?: string;
  expected_ipo_year?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtPrice(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${v.toFixed(2)}`;
}

function fmtMarketCap(v: number | null | undefined): string {
  if (v == null) return '—';
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return `$${v.toLocaleString()}`;
}

function fmtPct(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

function lockupLabel(info: LockupInfo): string {
  const days = info.days_until_lockup_expiry;
  switch (info.status) {
    case 'lockup_watch':
      return days > 0 ? `Lock-up expires in ${days}d` : `Lock-up expired ${Math.abs(days)}d ago`;
    case 'recent_ipo':
      return `IPO ${info.days_since_ipo}d ago`;
    case 'post_lockup':
      return `Post lock-up (${Math.abs(days)}d ago)`;
    default:
      return '';
  }
}

function lockupBadgeColor(status: string): 'warning' | 'error' | 'info' | 'success' | 'default' {
  switch (status) {
    case 'lockup_watch': return 'error';
    case 'recent_ipo': return 'warning';
    case 'post_lockup': return 'info';
    default: return 'default';
  }
}

function lockupRangePosition(info: LockupInfo): number {
  return Math.min(Math.max((info.days_since_ipo / 180) * 100, 0), 120);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PriceChange({ value, label }: { value: number | null | undefined; label: string }) {
  if (value == null) return null;
  const up = value >= 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography
        variant="caption"
        fontWeight="bold"
        color={up ? 'success.main' : 'error.main'}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}
      >
        {up ? <TrendingUp sx={{ fontSize: 12 }} /> : <TrendingDown sx={{ fontSize: 12 }} />}
        {fmtPct(value)}
      </Typography>
    </Box>
  );
}

function LockupBar({ info }: { info: LockupInfo }) {
  const pct = lockupRangePosition(info);
  const color = info.status === 'lockup_watch' ? 'error' : info.status === 'recent_ipo' ? 'warning' : 'info';
  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="caption" color="text.secondary">IPO</Typography>
        <Typography variant="caption" color="text.secondary">180d Lock-up</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(pct, 100)}
        color={color}
        sx={{ height: 6, borderRadius: 3 }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
        <Typography variant="caption" color="text.secondary">{info.ipo_date}</Typography>
        <Typography variant="caption" color="text.secondary">{info.lockup_expiry}</Typography>
      </Box>
    </Box>
  );
}

function BuzzBadge({ buzz }: { buzz: NewsBuzz }) {
  const count = buzz.article_count_7d;
  if (count === 0) return null;
  const intensity = count > 500 ? 'error' : count > 100 ? 'warning' : count > 20 ? 'info' : 'default';
  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" display="block" fontWeight="bold">
            {count} news articles in last 7 days
          </Typography>
          {buzz.latest_headline && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8, maxWidth: 280 }}>
              Latest: "{buzz.latest_headline.title.slice(0, 80)}…" — {buzz.latest_headline.source}
            </Typography>
          )}
        </Box>
      }
      arrow
    >
      <Chip
        size="small"
        icon={<Newspaper sx={{ fontSize: '12px !important' }} />}
        label={`${count} news`}
        color={intensity as any}
        sx={{ height: 18, fontSize: '0.65rem', cursor: 'help' }}
      />
    </Tooltip>
  );
}

// ── Pre-IPO Card ──────────────────────────────────────────────────────────────

function PreIpoCard({ company }: { company: PreIpoCompany }) {
  const isImminent = company.stage === 'ipo_imminent';
  const isRecent = company.stage === 'recent_ipo' || company.stage === 'just_ipost';
  const isPreIpo = company.stage === 'pre_ipo';

  const accentColor = isImminent ? '#F59E0B' : isRecent ? '#10B981' : '#60A5FA';
  const buzz = company.news_buzz;

  return (
    <Card
      sx={{
        border: '1px solid',
        borderColor: isImminent ? 'warning.dark' : isRecent ? 'success.dark' : 'divider',
        bgcolor: 'background.paper',
        position: 'relative',
        overflow: 'visible',
        transition: 'all 0.2s',
        '&:hover': { borderColor: accentColor, boxShadow: `0 0 16px ${accentColor}44` },
      }}
    >
      {/* Glowing top bar */}
      <Box sx={{ height: 4, bgcolor: accentColor, borderTopLeftRadius: 4, borderTopRightRadius: 4,
        boxShadow: isImminent ? `0 0 12px ${accentColor}` : 'none',
        animation: isImminent ? 'pulse 2s infinite' : 'none',
        '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } }
      }} />

      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Stage badge */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            {isImminent && (
              <Chip
                size="small"
                icon={<FlashOn sx={{ fontSize: '12px !important', color: '#F59E0B !important' }} />}
                label={company.days_to_ipo === 0 ? 'IPO TODAY' : `IPO in ${company.days_to_ipo}d`}
                sx={{ bgcolor: '#92400E', color: '#FCD34D', height: 20, fontSize: '0.68rem', fontWeight: 'bold' }}
              />
            )}
            {isRecent && (
              <Chip size="small" label="Recently IPO'd" color="success" sx={{ height: 20, fontSize: '0.68rem' }} />
            )}
            {isPreIpo && (
              <Chip
                size="small"
                icon={<HourglassBottom sx={{ fontSize: '12px !important' }} />}
                label={`Pre-IPO · Est. ${company.expected_ipo_year}`}
                sx={{ bgcolor: '#1e3a5f', color: '#93C5FD', height: 20, fontSize: '0.68rem' }}
              />
            )}
            <BuzzBadge buzz={buzz} />
          </Box>
        </Box>

        {/* Company name + ticker */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: accentColor, lineHeight: 1.1 }}>
              {company.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(company.ticker || company.expected_ticker)
                ? `${company.ticker || company.expected_ticker} · ${company.category}`
                : company.category}
            </Typography>
          </Box>
          {/* Price if trading */}
          {company.market_data?.is_trading && company.market_data.price && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body1" fontWeight="bold">{fmtPrice(company.market_data.price)}</Typography>
              {company.market_data.market_cap && (
                <Typography variant="caption" color="text.secondary">
                  Cap: {fmtMarketCap(company.market_data.market_cap)}
                </Typography>
              )}
            </Box>
          )}
          {/* Valuation if pre-IPO */}
          {!company.market_data?.is_trading && company.last_known_valuation_b && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.disabled">Private valuation</Typography>
              <Typography variant="body2" fontWeight="bold" color={accentColor}>
                ~${company.last_known_valuation_b}B
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.55, mb: 1.5 }}>
          {company.description}
        </Typography>

        {/* IPO signals */}
        {company.ipo_signals && (
          <Box sx={{ bgcolor: `${accentColor}11`, borderLeft: `3px solid ${accentColor}`, pl: 1, py: 0.5, mb: 1.5, borderRadius: '0 4px 4px 0' }}>
            <Typography variant="caption" fontWeight="bold" color={accentColor} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Public sx={{ fontSize: 12 }} /> IPO signals
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', lineHeight: 1.5 }}>
              {company.ipo_signals}
            </Typography>
          </Box>
        )}

        {/* Lock-up bar if IPO'd */}
        {company.lockup && company.lockup.status !== 'normal' && (
          <Box sx={{ mt: 1 }}>
            <Chip
              size="small"
              icon={<LockClock sx={{ fontSize: 12 }} />}
              label={lockupLabel(company.lockup)}
              color={lockupBadgeColor(company.lockup.status)}
              sx={{ mb: 1, fontSize: '0.68rem', height: 20 }}
            />
            <LockupBar info={company.lockup} />
          </Box>
        )}

        {/* Key investors */}
        {company.key_investors && company.key_investors.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.5 }}>
              Key investors
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {company.key_investors.map(inv => (
                <Chip key={inv} label={inv} size="small"
                  sx={{ fontSize: '0.6rem', height: 18, bgcolor: `${accentColor}22`, color: 'text.secondary' }} />
              ))}
            </Box>
          </Box>
        )}

        {/* Latest news headline */}
        {buzz.latest_headline && (
          <Box sx={{ mt: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.3 }}>
              Latest news ({buzz.latest_headline.source})
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
              {buzz.latest_headline.title.slice(0, 120)}{buzz.latest_headline.title.length > 120 ? '…' : ''}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ── Pre-IPO Section ───────────────────────────────────────────────────────────

function PreIpoSection({ companies }: { companies: PreIpoCompany[] }) {
  if (companies.length === 0) return null;
  const imminent = companies.filter(c => c.stage === 'ipo_imminent');
  const recent = companies.filter(c => c.stage === 'recent_ipo' || c.stage === 'just_ipost');
  const preIpo = companies.filter(c => c.stage === 'pre_ipo');

  return (
    <Box sx={{ mb: 3 }}>
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <FlashOn sx={{ color: '#F59E0B' }} />
        <Typography variant="h6" fontWeight="bold">
          Pre-IPO Watch & New Listings
        </Typography>
        {imminent.length > 0 && (
          <Chip
            label={`${imminent.length} IPO IMMINENT`}
            size="small"
            sx={{ bgcolor: '#92400E', color: '#FCD34D', fontWeight: 'bold', animation: 'pulse 2s infinite',
              '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.7 } } }}
          />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        Track the most anticipated IPOs before they happen and monitor the 180-day insider lock-up
        after listing. Sell pressure peaks around the lock-up expiry — watch for entry opportunities.
        News buzz is sourced from our live news scanner ({companies.reduce((n, c) => n + c.news_buzz.article_count_7d, 0)} articles in last 7 days).
      </Typography>

      {/* IPO Imminent */}
      {imminent.length > 0 && (
        <>
          <Typography variant="caption" fontWeight="bold" color="warning.main" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
            ⚡ IPO Imminent
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {imminent.map(c => (
              <Grid item xs={12} md={6} key={c.id}>
                <PreIpoCard company={c} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Recently IPO'd */}
      {recent.length > 0 && (
        <>
          <Typography variant="caption" fontWeight="bold" color="success.main" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
            ✅ Recently IPO'd — Lock-up Watch
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {recent.map(c => (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <PreIpoCard company={c} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Pre-IPO pipeline */}
      {preIpo.length > 0 && (
        <>
          <Typography variant="caption" fontWeight="bold" color="info.main" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
            🔭 Pre-IPO Pipeline
          </Typography>
          <Grid container spacing={2}>
            {preIpo.map(c => (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <PreIpoCard company={c} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
}

// ── Discovery Section ─────────────────────────────────────────────────────────

function RadarCompanyRow({ c, accent, showTheme }: { c: any; accent: string; showTheme?: boolean }) {
  return (
    <Box sx={{
      p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: `${accent}33`,
      bgcolor: `${accent}08`,
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight="bold">{c.company_name}</Typography>
          {showTheme && c.theme && (
            <Chip size="small" label={c.theme}
              sx={{ height: 16, fontSize: '0.58rem', bgcolor: `${accent}22`, color: accent }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {c.context && (
            <Chip size="small" label={c.context.replace(/·/g, '·')}
              sx={{ height: 16, fontSize: '0.58rem', bgcolor: '#10B98122', color: '#10B981', maxWidth: 150 }} />
          )}
          <Chip size="small" label={`${c.mention_count}x`}
            sx={{ height: 16, fontSize: '0.6rem', bgcolor: `${accent}22`, color: accent }} />
        </Box>
      </Box>
      {c.note && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem', display: 'block', mt: 0.3 }}>
          {c.note}
        </Typography>
      )}
      {c.latest_headline && (
        <Typography variant="caption" sx={{ fontSize: '0.68rem', lineHeight: 1.4, mt: 0.5, display: 'block' }}>
          {c.latest_url ? (
            <Link href={c.latest_url} target="_blank" rel="noopener noreferrer"
              sx={{ color: 'text.secondary', textDecorationColor: `${accent}66`, '&:hover': { color: accent } }}>
              {c.latest_headline}
            </Link>
          ) : (
            <span style={{ opacity: 0.85 }}>{c.latest_headline}</span>
          )}
          {c.latest_source && <span style={{ opacity: 0.5 }}> — {c.latest_source}</span>}
        </Typography>
      )}
    </Box>
  );
}

function DiscoverySection({ discovery, onRefresh, scanning }: {
  discovery: any; onRefresh: () => void; scanning: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!discovery) return null;

  const watchlistCompanies = discovery.watchlist_companies ?? [];
  const newCompanies = discovery.new_companies ?? [];
  const emergingThemes = discovery.emerging_themes ?? [];
  const headlines = discovery.top_ipo_headlines ?? [];
  const scannedCount = discovery.articles_scanned ?? 0;

  const newCategoryCount = emergingThemes.filter((t: any) => t.is_new_category).length;
  const totalCompanies = watchlistCompanies.length + newCompanies.length;

  return (
    <Accordion
      expanded={open}
      onChange={() => setOpen(o => !o)}
      sx={{
        mb: 3, bgcolor: 'background.paper',
        border: '1px solid', borderColor: open ? '#7C3AED' : 'divider',
        borderRadius: '8px !important', '&:before': { display: 'none' },
        boxShadow: open ? '0 0 20px #7C3AED22' : 'none', transition: 'all 0.2s',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#7C3AED' }} />}
        sx={{ borderRadius: open ? '8px 8px 0 0' : '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
          <Radar sx={{ color: '#7C3AED', fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography fontWeight="bold" sx={{ color: open ? '#7C3AED' : 'text.primary' }}>
                Discovery Radar — New Companies &amp; Categories
              </Typography>
              {totalCompanies > 0 && (
                <Chip label={`${totalCompanies} companies`} size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#7C3AED22', color: '#7C3AED' }} />
              )}
              {newCategoryCount > 0 && (
                <Chip icon={<AddCircleOutline sx={{ fontSize: '10px !important', color: '#10B981 !important' }} />}
                  label={`${newCategoryCount} new categories`} size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#10B98122', color: '#10B981' }} />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Scanned {scannedCount.toLocaleString()} articles · {discovery.scan_period_days}d window · Last: {
                discovery.last_scanned ? new Date(discovery.last_scanned).toLocaleTimeString() : '—'}
            </Typography>
          </Box>
          <Button size="small" variant="outlined"
            startIcon={scanning ? <CircularProgress size={12} color="inherit" /> : <Refresh />}
            onClick={e => { e.stopPropagation(); onRefresh(); }} disabled={scanning}
            sx={{ color: '#7C3AED', borderColor: '#7C3AED55', fontSize: '0.7rem', py: 0.3 }}>
            {scanning ? 'Scanning…' : 'Re-scan'}
          </Button>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: { xs: 1.5, md: 2.5 }, pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          Automatically mined from {scannedCount.toLocaleString()} recent news articles.
          <strong> Notable private companies</strong> tracks well-known pre-IPO names by news volume;
          <strong> newly detected events</strong> surfaces companies that just announced a funding round or
          IPO filing (each verified to lead a headline with a real dollar amount).
          <strong> Emerging themes</strong> are promoted to real categories automatically: once a theme
          appears in {discovery.auto_promotion?.min_headlines ?? 5} distinct headlines and the LLM agent confirms it is a
          coherent, investable technology, it is added above with a generated description, and the same agent that
          reads the news then files stocks into it. Research starting point — verify before investing.
        </Typography>

        <Grid container spacing={2.5}>
          {/* Companies on the radar */}
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" fontWeight="bold" color="#7C3AED" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpOutlined sx={{ fontSize: 16 }} /> Notable Private Companies in the News
            </Typography>
            {watchlistCompanies.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>No watchlist mentions this period.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {watchlistCompanies.map((c: any, i: number) => (
                  <RadarCompanyRow key={`w${i}`} c={c} accent="#7C3AED" showTheme />
                ))}
              </Box>
            )}

            <Typography variant="subtitle2" fontWeight="bold" color="#F59E0B" sx={{ mb: 1, mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Bolt sx={{ fontSize: 16 }} /> Newly Detected Funding / IPO Events
            </Typography>
            {newCompanies.length === 0 ? (
              <Typography variant="body2" color="text.disabled">No new funding events detected this period.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {newCompanies.map((c: any, i: number) => (
                  <RadarCompanyRow key={`n${i}`} c={c} accent="#F59E0B" />
                ))}
              </Box>
            )}
          </Grid>

          {/* Emerging themes */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight="bold" color="#10B981" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AddCircleOutline sx={{ fontSize: 16 }} /> Emerging Themes / New Categories
            </Typography>
            {emergingThemes.length === 0 ? (
              <Typography variant="body2" color="text.disabled">No new themes detected.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {emergingThemes.map((t: any, i: number) => (
                  <Box key={i} sx={{
                    p: 1.5, borderRadius: 1.5, border: '1px solid',
                    borderColor: t.is_new_category ? '#10B98144' : 'divider',
                    bgcolor: t.is_new_category ? '#10B98108' : 'transparent',
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                        {t.theme}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip size="small" label={`${t.mention_count} mentions`}
                          sx={{ height: 16, fontSize: '0.6rem' }} />
                        {t.is_new_category && (
                          <Chip size="small" label="NEW" color="success"
                            sx={{ height: 16, fontSize: '0.6rem', fontWeight: 'bold' }} />
                        )}
                      </Box>
                    </Box>
                    <Typography variant="caption" color={t.is_new_category ? 'success.main' : 'text.secondary'}
                      sx={{ fontSize: '0.68rem', mt: 0.5, display: 'block' }}>
                      {t.recommendation}
                    </Typography>
                    {t.examples?.[0] && (
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.3 }}>
                        e.g. "{t.examples[0].title?.slice(0, 70)}…"
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Top IPO headlines */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" fontWeight="bold" color="warning.main" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Newspaper sx={{ fontSize: 16 }} /> Top IPO Headlines
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {headlines.map((h: any, i: number) => (
                <Box key={i} sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
                    {h.title?.slice(0, 90)}{h.title?.length > 90 ? '…' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.62rem', mt: 0.2 }}>
                    {h.source} · {h.published_at ? new Date(h.published_at).toLocaleDateString() : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

// ── Stock Card (for categories) ───────────────────────────────────────────────

function StockCard({ stock, categoryColor }: { stock: Stock; categoryColor: string }) {
  const md = stock.market_data;
  const lockup = stock.lockup;
  const [showFull, setShowFull] = useState(false);
  const hasLockup = lockup && lockup.status !== 'normal' && lockup.status !== 'pre_ipo';
  const isPreIpo = stock.stage === 'pre_ipo';
  const isImminent = stock.stage === 'ipo_imminent';
  // Classifier-added stocks carry the LLM's one-line reasoning as their disruption text
  // and may carry no stage/tags at all; never let a missing field blank the card.
  const description = stock.description ?? '';
  const disruption = stock.disruption ?? '';
  const fromNews = stock.source === 'classifier';
  const retired = stock.status === 'retired';
  const pulse = stock.pulse;

  return (
    <Card
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: isImminent ? 'warning.dark' : hasLockup && lockup.status === 'lockup_watch'
          ? 'error.dark' : 'divider',
        bgcolor: 'background.paper',
        position: 'relative',
        transition: 'all 0.2s',
        opacity: retired ? 0.55 : 1,
        '&:hover': { borderColor: categoryColor, boxShadow: `0 0 12px ${categoryColor}33` },
      }}
    >
      {(isImminent || (hasLockup && lockup.status === 'lockup_watch')) && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          bgcolor: isImminent ? 'warning.main' : 'error.main',
          borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit',
        }} />
      )}

      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Typography variant="h6" fontWeight="bold" lineHeight={1} sx={{ color: categoryColor }}>
                {isPreIpo ? '—' : stock.symbol}
              </Typography>
              {isImminent && <Chip size="small" label="IPO SOON" sx={{ bgcolor: '#92400E', color: '#FCD34D', height: 16, fontSize: '0.6rem' }} />}
              {isPreIpo && <Chip size="small" label="PRE-IPO" sx={{ bgcolor: '#1e3a5f', color: '#93C5FD', height: 16, fontSize: '0.6rem' }} />}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {stock.name}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {md?.data_available && !isPreIpo ? (
              <>
                <Typography variant="body2" fontWeight="bold">{fmtPrice(md.price)}</Typography>
                <PriceChange value={md.change_pct} label="1d" />
                <PriceChange value={md.week_change_pct} label="5d" />
              </>
            ) : isPreIpo ? (
              <Typography variant="caption" color="info.main">Private</Typography>
            ) : (
              <Typography variant="caption" color="text.disabled">No data</Typography>
            )}
          </Box>
        </Box>

        {md?.data_available && !isPreIpo && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
            <Tooltip title="Market Capitalization">
              <Typography variant="caption" color="text.secondary">
                Cap: <strong>{fmtMarketCap(md.market_cap)}</strong>
              </Typography>
            </Tooltip>
            {md.fifty_two_week_low != null && md.fifty_two_week_high != null && (
              <Tooltip title="52-Week Range">
                <Typography variant="caption" color="text.secondary">
                  52w: <strong>{fmtPrice(md.fifty_two_week_low)} – {fmtPrice(md.fifty_two_week_high)}</strong>
                </Typography>
              </Tooltip>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.5, mb: 1 }}>
          {showFull ? description : description.slice(0, 140) + (description.length > 140 ? '…' : '')}
        </Typography>

        <Box sx={{ bgcolor: `${categoryColor}11`, borderLeft: `3px solid ${categoryColor}`, pl: 1, py: 0.5, mb: 1, borderRadius: '0 4px 4px 0' }}>
          <Typography variant="caption" fontWeight="bold" color={categoryColor} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Bolt sx={{ fontSize: 12 }} /> Why it's a disruptor
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', lineHeight: 1.5 }}>
            {showFull ? disruption : disruption.slice(0, 120) + (disruption.length > 120 ? '…' : '')}
          </Typography>
        </Box>

        {description.length > 140 && (
          <Button size="small" onClick={() => setShowFull(f => !f)}
            sx={{ fontSize: '0.7rem', p: 0, minWidth: 0, textTransform: 'none', color: categoryColor }}>
            {showFull ? 'Show less' : 'Read more'}
          </Button>
        )}

        {hasLockup && (
          <Box sx={{ mt: 1.5 }}>
            <Chip size="small" icon={<LockClock sx={{ fontSize: 12 }} />}
              label={lockupLabel(lockup!)} color={lockupBadgeColor(lockup!.status)}
              sx={{ mb: 1, fontSize: '0.68rem', height: 20 }} />
            <LockupBar info={lockup!} />
          </Box>
        )}

        {retired && (
          <Chip label={`retired · ${stock.retired_reason || 'no longer tracked'}`} size="small" color="error" variant="outlined"
            sx={{ mt: 1.5, height: 18, fontSize: '0.62rem' }} />
        )}
        {pulse && (pulse.news_30d != null || pulse.last_headline) && (
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Headlines naming this company in the last 30 days">
                <Chip label={`${pulse.news_30d ?? 0} headlines / 30d`} size="small"
                  sx={{ height: 18, fontSize: '0.62rem', bgcolor: (pulse.news_30d ?? 0) > 0 ? `${categoryColor}22` : 'transparent', color: 'text.secondary' }} />
              </Tooltip>
              {pulse.last_news_at && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>latest {pulse.last_news_at.slice(0, 10)}</Typography>
              )}
            </Box>
            {pulse.last_headline && (
              <Typography component={pulse.last_headline_url ? 'a' : 'span'} href={pulse.last_headline_url || undefined} target="_blank" rel="noopener noreferrer"
                variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', textDecoration: 'none', lineHeight: 1.4, '&:hover': { color: categoryColor } }}>
                {pulse.last_headline_source ? pulse.last_headline_source + ': ' : ''}{pulse.last_headline.slice(0, 90)}{pulse.last_headline.length > 90 ? '…' : ''}
              </Typography>
            )}
          </Box>
        )}
        {(fromNews || stock.source === 'manual') && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Tooltip title={`Added by the news-reading LLM agent${stock.evidence?.confidence ? ` (confidence ${Math.round(stock.evidence.confidence * 100)}%)` : ''}${stock.added_at ? ' on ' + stock.added_at.slice(0, 10) : ''}`}>
              <Chip icon={<Newspaper sx={{ fontSize: 11 }} />}
                label={stock.source === 'manual' ? 'added manually' : stock.evidence?.origin === 'name_basket' ? 'from news · by name' : 'from news'}
                size="small" variant="outlined"
                sx={{ height: 18, fontSize: '0.62rem', color: '#F59E0B', borderColor: '#F59E0B66' }} />
            </Tooltip>
            {stock.evidence?.article_url && (
              <Typography component="a" href={stock.evidence.article_url} target="_blank" rel="noopener noreferrer"
                variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 0.3, textDecoration: 'none', '&:hover': { color: categoryColor } }}>
                {(stock.evidence.article_source || 'source')}: {(stock.evidence.article_title || '').slice(0, 60)}{(stock.evidence.article_title || '').length > 60 ? '…' : ''}
                <OpenInNew sx={{ fontSize: 10 }} />
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
          {(stock.tags ?? []).map(tag => (
            <Chip key={tag} label={tag} size="small"
              sx={{ fontSize: '0.6rem', height: 18, bgcolor: `${categoryColor}22`, color: 'text.secondary' }} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function LockupWatchBanner({ stocks }: { stocks: any[] }) {
  const watchStocks = stocks.filter(s => s.lockup?.status === 'lockup_watch');
  if (watchStocks.length === 0) return null;

  return (
    <Alert severity="warning" icon={<LockClock />}
      sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Lock-up Expiry Watch — {watchStocks.length} stock{watchStocks.length > 1 ? 's' : ''} near 180-day mark
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Early investors and insiders can sell after 180 days. Price pressure common around expiry —
        can be a buying opportunity once selling subsides.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {watchStocks.map(s => (
          <Box key={s.symbol} sx={{ display: 'flex', alignItems: 'center', gap: 0.8,
            bgcolor: 'warning.dark', px: 1.5, py: 0.5, borderRadius: 2 }}>
            <Typography variant="caption" fontWeight="bold" color="warning.contrastText">{s.symbol}</Typography>
            <Typography variant="caption" color="warning.contrastText" sx={{ opacity: 0.85 }}>
              {lockupLabel(s.lockup)}
            </Typography>
            {s.market_data?.change_pct != null && (
              <Typography variant="caption"
                sx={{ color: s.market_data.change_pct >= 0 ? 'success.light' : 'error.light', fontWeight: 'bold' }}>
                {fmtPct(s.market_data.change_pct)}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Alert>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function FutureStocks() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [lockupStocks, setLockupStocks] = useState<any[]>([]);
  const [preIpoCompanies, setPreIpoCompanies] = useState<PreIpoCompany[]>([]);
  const [discovery, setDiscovery] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [discoveryScanning, setDiscoveryScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>('generative_ai_platforms');

  const load = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
        await apiService.refreshFutureStocks();
      }

      // Phase 1: load static data + lockup/pre-ipo/discovery instantly (no yfinance calls)
      const [catStatic, lockupData, preIpoData, discoveryData] = await Promise.all([
        apiService.getFutureStocksCategories(false),
        apiService.getFutureStocksLockupWatch(),
        apiService.getPreIpoCompanies(),
        apiService.getFutureStocksDiscovery(14, false),
      ]);
      setCategories(catStatic.categories ?? []);
      setLockupStocks(lockupData.stocks ?? []);
      setPreIpoCompanies(preIpoData.companies ?? []);
      setDiscovery(discoveryData);
      setError(null);
      setLoading(false);

      // Phase 2: enrich with live prices in background (uses cache if warm, ~1s; cold ~25s)
      setEnriching(true);
      try {
        const catEnriched = await apiService.getFutureStocksCategories(true);
        setCategories(catEnriched.categories ?? []);
      } catch {
        // live prices failed — static data already shown, not critical
      } finally {
        setEnriching(false);
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to load future stocks');
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const runDiscoveryScan = useCallback(async () => {
    setDiscoveryScanning(true);
    try {
      const data = await apiService.getFutureStocksDiscovery(14, true);
      setDiscovery(data);
    } catch (e: any) {
      setError('Discovery scan failed: ' + (e.message || 'unknown error'));
    } finally {
      setDiscoveryScanning(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAccordion = (id: string) => (_: React.SyntheticEvent, isOpen: boolean) => {
    setExpanded(isOpen ? id : false);
  };

  const totalNewsToday = preIpoCompanies.reduce((n, c) => n + c.news_buzz.article_count_7d, 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, gap: 2 }}>
        <CircularProgress size={48} />
        <Typography color="text.secondary">Loading future stocks…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>

      {/* Hero header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0d1b2a 0%, #1a2744 40%, #0d1b2a 100%)',
        border: '1px solid #ffffff18', borderRadius: 3, p: { xs: 2.5, md: 4 }, mb: 3,
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.06, fontSize: 200, lineHeight: 1, pointerEvents: 'none' }}>
          🚀
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <RocketLaunch sx={{ color: '#60A5FA', fontSize: 28 }} />
              <Typography variant="h4" fontWeight="bold" color="white">Stocks of the Future</Typography>
            </Box>
            <Typography color="rgba(255,255,255,0.7)" sx={{ maxWidth: 680, lineHeight: 1.7 }}>
              10 thematic categories shaping the next decade — including pre-IPO tracking for
              SpaceX, OpenAI, Anthropic &amp; more. Live prices, news signals from {totalNewsToday}+ articles
              this week, and 180-day lock-up monitoring for every new listing.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
              <Chip label={`${categories.length} Categories`} size="small" sx={{ bgcolor: '#ffffff22', color: 'white' }} />
              <Chip label={`${categories.reduce((n, c) => n + c.stocks.length, 0)} Stocks`}
                size="small" sx={{ bgcolor: '#ffffff22', color: 'white' }} />
              {preIpoCompanies.filter(c => c.stage === 'ipo_imminent').length > 0 && (
                <Chip
                  icon={<FlashOn sx={{ fontSize: 14, color: '#FCD34D !important' }} />}
                  label={`${preIpoCompanies.filter(c => c.stage === 'ipo_imminent').length} IPO Imminent`}
                  size="small" sx={{ bgcolor: '#92400E', color: '#FCD34D', fontWeight: 'bold' }}
                />
              )}
              {lockupStocks.filter(s => s.lockup?.status === 'lockup_watch').length > 0 && (
                <Chip
                  icon={<LockClock sx={{ fontSize: 14, color: '#FCD34D !important' }} />}
                  label={`${lockupStocks.filter(s => s.lockup?.status === 'lockup_watch').length} Lock-up Watch`}
                  size="small" sx={{ bgcolor: '#92400E', color: '#FCD34D' }}
                />
              )}
            </Box>
          </Box>
          <Button variant="outlined"
            startIcon={(refreshing || enriching) ? <CircularProgress size={14} color="inherit" /> : <Refresh />}
            onClick={() => load(true)} disabled={refreshing || enriching}
            sx={{ color: 'white', borderColor: '#ffffff44', '&:hover': { borderColor: 'white' }, whiteSpace: 'nowrap' }}>
            {refreshing ? 'Refreshing…' : enriching ? 'Loading prices…' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Pre-IPO Watch section */}
      <PreIpoSection companies={preIpoCompanies} />

      {/* Discovery Radar */}
      <DiscoverySection discovery={discovery} onRefresh={runDiscoveryScan} scanning={discoveryScanning} />

      {/* Lock-up watch banner */}
      <LockupWatchBanner stocks={lockupStocks} />

      {/* Recent IPO chips */}
      {lockupStocks.filter(s => s.lockup?.status === 'recent_ipo').length > 0 && (
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Recent IPOs on Watch</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            {lockupStocks.filter(s => s.lockup?.status === 'recent_ipo').map(s => (
              <Chip key={s.symbol} size="small"
                label={`${s.symbol} — ${lockupLabel(s.lockup)} (${s.category})`}
                color="info" variant="outlined" sx={{ fontSize: '0.7rem' }} />
            ))}
          </Box>
        </Alert>
      )}

      {/* Category accordions */}
      {categories.map(cat => {
        const isOpen = expanded === cat.id;
        const catLockupCount = cat.stocks.filter(s =>
          s.lockup && ['lockup_watch', 'recent_ipo', 'ipo_imminent'].includes(s.lockup.status)
        ).length;
        const catPreIpoCount = cat.stocks.filter(s => ['pre_ipo', 'ipo_imminent'].includes(s.stage)).length;
        const catFromNews = cat.stocks.filter(s => s.source === 'classifier').length;

        return (
          <Accordion key={cat.id} expanded={isOpen} onChange={handleAccordion(cat.id)}
            sx={{
              mb: 1.5, bgcolor: 'background.paper', border: '1px solid',
              borderColor: isOpen ? cat.color : 'divider',
              borderRadius: '8px !important', '&:before': { display: 'none' },
              boxShadow: isOpen ? `0 0 20px ${cat.color}22` : 'none', transition: 'all 0.2s',
            }}>
            <AccordionSummary expandIcon={<ExpandMore sx={{ color: cat.color }} />}
              sx={{ borderRadius: isOpen ? '8px 8px 0 0' : '8px', '&.Mui-expanded': { minHeight: 56 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                <Typography sx={{ fontSize: '1.4rem', lineHeight: 1 }}>{cat.emoji}</Typography>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography fontWeight="bold" sx={{ color: isOpen ? cat.color : 'text.primary' }}>
                      {cat.name}
                    </Typography>
                    <Chip label={`${cat.stocks.length} stocks`} size="small"
                      sx={{ height: 18, fontSize: '0.65rem', bgcolor: `${cat.color}22`, color: cat.color }} />
                    {cat.source === 'discovered' && (
                      <Tooltip title={`Created automatically by the discovery radar${cat.created_at ? ' on ' + cat.created_at.slice(0, 10) : ''} — description written by the LLM agent`}>
                        <Chip icon={<AutoAwesome sx={{ fontSize: 10 }} />} label="auto-discovered" size="small" color="success" variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }} />
                      </Tooltip>
                    )}
                    {cat.pulse?.news_30d != null && cat.pulse.news_30d > 0 && (
                      <Tooltip title="Distinct headlines matching this theme in the last 30 days">
                        <Chip icon={<Newspaper sx={{ fontSize: 10 }} />} label={`${cat.pulse.news_30d} headlines / 30d`} size="small" variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', color: 'text.secondary' }} />
                      </Tooltip>
                    )}
                    {catFromNews > 0 && (
                      <Tooltip title="Stocks the news-reading LLM agent added to this category">
                        <Chip label={`+${catFromNews} from news`} size="small" variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', color: '#F59E0B', borderColor: '#F59E0B66' }} />
                      </Tooltip>
                    )}
                    {catPreIpoCount > 0 && (
                      <Chip label={`${catPreIpoCount} pre-IPO`} size="small"
                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#1e3a5f', color: '#93C5FD' }} />
                    )}
                    {catLockupCount > 0 && (
                      <Chip icon={<LockClock sx={{ fontSize: 10, color: 'warning.main !important' }} />}
                        label={`${catLockupCount} IPO watch`} size="small" color="warning" variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  {!isOpen && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                      {cat.description.slice(0, 110)}…
                    </Typography>
                  )}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: { xs: 1.5, md: 2.5 }, pt: 0 }}>
              <Box sx={{ mb: 2.5, p: 2, bgcolor: `${cat.color}0a`, borderRadius: 2, border: `1px solid ${cat.color}22` }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1.5 }}>
                  {cat.description}
                </Typography>
                <Box sx={{ borderLeft: `3px solid ${cat.color}`, pl: 1.5 }}>
                  <Typography variant="caption" fontWeight="bold" color={cat.color}>Why it matters now</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
                    {cat.why_it_matters}
                  </Typography>
                </Box>
                {cat.pulse?.text && (
                  <Box sx={{ mt: 1.5, borderLeft: '3px solid #F59E0B', pl: 1.5 }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: '#F59E0B', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Newspaper sx={{ fontSize: 12 }} /> This month in {cat.name}
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ fontWeight: 'normal', fontSize: '0.62rem' }}>
                        · {cat.pulse.news_30d ?? 0} headlines / 30d · written by the news agent{cat.pulse.updated_at ? ' on ' + cat.pulse.updated_at.slice(0, 10) : ''}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
                      {cat.pulse.text}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Grid container spacing={2}>
                {cat.stocks.map(stock => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={stock.symbol}>
                    <StockCard stock={stock} categoryColor={cat.color} />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Footer */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InfoOutlined sx={{ fontSize: 14 }} />
          Prices refresh every hour. Lock-up = IPO date + 180 days (standard insider period).
          News buzz sourced from live news scanner database. Not financial advice.
        </Typography>
      </Box>
    </Box>
  );
}
